// -----------------------------------------------------------------------------
// engine/media/frame-cache.ts — a small LRU cache of decoded frames.
//
// Decoding a video frame (seek + decode) is expensive, so we keep the most
// recently used frames as ImageBitmaps. During scrubbing/playback the same
// nearby frames are needed repeatedly; this avoids re-decoding them. Evicted
// bitmaps are closed to free their backing memory promptly.
// -----------------------------------------------------------------------------

export class FrameCache {
  // Map preserves insertion order; we use that to find the least-recently-used.
  private map = new Map<string, ImageBitmap>();

  constructor(private readonly capacity = 48) {}

  get(key: string): ImageBitmap | null {
    const bmp = this.map.get(key);
    if (!bmp) return null;
    // Mark as most-recently-used by reinserting.
    this.map.delete(key);
    this.map.set(key, bmp);
    return bmp;
  }

  set(key: string, bmp: ImageBitmap): void {
    if (this.map.has(key)) {
      this.map.delete(key);
    }
    this.map.set(key, bmp);
    while (this.map.size > this.capacity) {
      const oldestKey = this.map.keys().next().value as string | undefined;
      if (oldestKey === undefined) break;
      const old = this.map.get(oldestKey);
      this.map.delete(oldestKey);
      old?.close();
    }
  }

  clear(): void {
    for (const bmp of this.map.values()) bmp.close();
    this.map.clear();
  }
}
